import { useAtomValue } from 'jotai';
import { type FC } from 'react';

import { useAdmissionFlow, useIsAutomaticAdmissionEnabled } from '../../../hooks/useAdmissionFlow';
import { useVideoPlatform } from '../../../hooks/useVideoPlatform';
import { useGetClientSafetyGuard } from '../../../useGetClientSafetyGuard';
import { VideoPlatform } from '../../../video-sdk';
import { videoRoomTokenAtom } from '../../../video-sdk/core/room/state';
import { VisitorDrawerState, visitorMeasuresAtom } from '../../state';
import { WaitingRoomState } from '../types';
import { JoinSessionCta } from './JoinSessionCta';
import { CTAProps, PreSessionPrompt, PreSessionPromptVariant, VariantComponents } from './PreSessionPrompt';

export function PreSessionPromptWrapper({
  isLoading,
  variant,
  openDrawer,
}: {
  isLoading: boolean;
  variant?: PreSessionPromptVariant;
  openDrawer: (drawer: VisitorDrawerState) => void;
}) {
  const admissionFlow = useAdmissionFlow();
  const hasRoomToken = !!useAtomValue(videoRoomTokenAtom);
  // TODO(telehealth_automatic_admission_xp): remove if the Test group ships
  const isAdmitted = admissionFlow === 'manual' && hasRoomToken;
  const isAutomaticAdmissionEnabled = useIsAutomaticAdmissionEnabled();
  const isZoomManualFlow = useVideoPlatform() === VideoPlatform.Zoom && !isAutomaticAdmissionEnabled;
  const visitorMeasures = useAtomValue(visitorMeasuresAtom);
  const { requiresSafetyGuards } = useGetClientSafetyGuard();
  const showOutstandingMeasures = !!visitorMeasures?.length && requiresSafetyGuards === false;

  let CallToAction: FC<CTAProps> | null = null;
  if (isAdmitted) {
    CallToAction = JoinSessionCta;
    // We suppress all CTAs except for the join session CTA when the client has
    // outstanding measures to encourage measure completion.
  } else if (showOutstandingMeasures) {
    CallToAction = null;
  } else if (isLoading) {
    // We also suppress the CTA while the page is loading, in case new data
    // coming in changes the decision about which CTA to show.
    CallToAction = null;
  } else if (variant) {
    CallToAction = VariantComponents[variant];
  }

  const state = isAdmitted ? WaitingRoomState.ADMITTED : WaitingRoomState.AWAITING_ADMISSION;

  return (
    <PreSessionPrompt
      callToActionContent={
        CallToAction ? <CallToAction openDrawer={openDrawer} state={state} variant={variant} /> : null
      }
      hasOutstandingMeasures={showOutstandingMeasures}
      isZoomManualFlow={isZoomManualFlow}
      state={state}
    />
  );
}
